import type { NostrEvent } from '@nostrify/nostrify';
import { WikiContent } from '@/components/WikiContent';
import type { ViewFormat } from '@/hooks/useContentFormat';
import { cn } from '@/lib/utils';

interface ContentBodyProps {
  /** The full published event — its `content` is rendered normally, and the whole event is shown as JSON in the "Raw" view. */
  event: NostrEvent;
  /** The format to render with — typically `useContentFormat(event.content).selected`. */
  format: ViewFormat;
  className?: string;
  /** Extra classes for the raw <pre> view specifically. */
  rawClassName?: string;
}

/**
 * Renders a wiki article's or publication section's event in the given
 * `format` — any detected/overridden `ContentFormat` (Djot / AsciiDoc /
 * Markdown / Plain text) applied to `event.content`, or `'raw'` for the
 * exact published event as JSON (kind, tags, signature, and all —not
 * just the `content` field), so readers can verify precisely what was
 * signed and published.
 *
 * Pairs with `useContentFormat` (detection + override state) and
 * `ContentFormatSelect` (the dropdown UI for picking a format, including
 * "Raw") — those two are split out so callers can place the selector
 * anywhere in the page layout (e.g. below the action buttons) rather
 * than always directly above the content body.
 */
export function ContentBody({ event, format, className, rawClassName }: ContentBodyProps) {
  if (format === 'raw') {
    return (
      <pre className={cn('overflow-x-auto rounded-lg bg-muted p-4 text-sm whitespace-pre-wrap', className, rawClassName)}>
        {JSON.stringify(event, null, 2)}
      </pre>
    );
  }

  return <WikiContent content={event.content} format={format} className={className} />;
}
